import Term from "../../term/Term";
import styles from "../css/MultipleChoice.module.css";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import SpeakButton from "../../common/SpeakButton";
import { fetchGroupLanguageCode } from "../../groups/groupAPI";
import {
  EnglishPronunciation,
  getVoiceFile,
  makeBase64String,
} from "../../voice/util/voice";
import { useEffect, useRef, useState } from "react";
import {
  getLearningAnswerSpeechFromLocalStroage,
  getMemoPositionFromLocalStorage,
  getLearningPronunciationPositionFromLocalStroage,
  getQuestionTypeFromLocalStorage,
  isOnLearningTextToSpeechFromLocalStorage,
  isOnWrongAnswerNoteFromLocalStorage,
} from "../../settings/util/learningSettings";
import { LearningType } from "../model/LearningType";
import { QuestionType } from "../../settings/model/QuestionType";
import { auth } from "../../../configurations/firebaseConfig";
import { PronunciationPosition } from "../../settings/model/PronunciationPosition";
import { MultipleChoiceAnswerButton } from "./MultipleChoiceAnswerButton";
import classNames from "classnames";
import { delay } from "lodash";
import AudioPlayer from "react-h5-audio-player";
import EnterKeyListenerComponent from "../../common/keyListeners";
import { isHiraganaOnly } from "../../common/strings";
import { useReadingSettings } from "../../common/util/chineseCharacterMode";
import PronunciationSelectView from "../../common/view/PronunciationSelectView";
import MarkButton from "../../common/MarkButton";
import { formatShortcut } from "../../common/util/shourtcut";
import { MemoPosition } from "../../settings/model/MemoPosition";
import LearningTimeLimitView from "./LearningTimeLimitView";
import { getMultipleChoiceTimeLimitValue } from "../util/learning";
import { LearningAnswerSpeech } from "../model/LearningAnswerSpeech";
import WrongAnswerNote from "./WrongAnswerNote";

interface MultipleChoiceProps {
  term: Term;
  answers: Term[];
  englishPronunciation: EnglishPronunciation;
  showingPronunciationSelectView: boolean;
  isPaused: boolean;
  onClickMarkButton: (term: Term) => void;
  onCorrect: (term: Term) => void;
  onIncorrect: (term: Term) => void;
  onSpeakError: (error: string) => void;
  onClickPronunciationSelectButton: (
    pronunciation: EnglishPronunciation
  ) => void;
}

export default function MultipleChoice(props: MultipleChoiceProps) {
  const { t } = useTranslation();
  const { chineseCharacterMode } = useReadingSettings();

  const [audioSrc, setAudioSrc] = useState<string>();
  const randomChoice = Math.random() < 0.5 ? "term" : "definition";
  const [currentQuestion, setCurrentQuestion] = useState<
    "term" | "definition" | "pron"
  >(getCurrentQuestionType());

  const [question, setQuestion] = useState("");
  const [answers, setAnswers] = useState<string[]>([]);
  const [questionUpdateCount, setQuestionUpdateCount] = useState(0);
  const [currentGroup, setCurrentGroup] = useState("");
  const [currentGroupUpdateCount, setCurrentGroupUpdateCount] = useState(0);
  const [speakCount, setSpeakCount] = useState(0);

  const [timeLimitSecond, setTimeLimitSecond] = useState(
    getMultipleChoiceTimeLimitValue()
  );
  const [timeLimitRestartToken, setTimeLimitRestartToken] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isResultMode, setIsResultMode] = useState(false);
  const [isAnswerResult, setIsAnswerResult] = useState(false);
  // 고른 보기 (모르겠어요/시간 초과면 null)
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [isShowingWrongAnswerNote, setIsShowingWrongAnswerNote] =
    useState(false);

  const speechShourtCut = formatShortcut({ mac: "⌘ S", win: "Ctrl + S" });

  // 같은 카드에서 단어를 편집하거나 별표를 눌러도 진행 상태가 초기화되지 않도록 id 가 바뀔 때만 새로 시작한다
  useEffect(() => {
    reload();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.term.id]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const isSave = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s";

      if (!isSave) return;

      e.preventDefault();
      speak();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const list: string[] = [];
    switch (currentQuestion) {
      case "term":
      case "pron":
        props.answers.forEach((value: Term) => {
          list.push(value.definition);
        });
        break;
      case "definition":
        props.answers.forEach((value: Term) => {
          list.push(value.term);
        });
        break;
    }
    setAnswers(list);
  }, [props.answers, currentQuestion]);

  useEffect(() => {
    setCurrentGroupUpdateCount(currentGroupUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGroup]);

  useEffect(() => {
    setQuestionUpdateCount(questionUpdateCount + 1);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (
        speakCount === 0 &&
        currentGroupUpdateCount > 0 &&
        questionUpdateCount > 0 &&
        auth.currentUser !== null
      ) {
        if (!isOnLearningTextToSpeechFromLocalStorage()) return;
        if (currentQuestion === "definition") return;
        speak();
      }
    }, 50);

    return () => {
      clearTimeout(handler);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question, currentGroup]);

  useEffect(() => {
    let newQuestionString = "";
    let newQuestionGroupString = "";
    switch (currentQuestion) {
      case "term":
        newQuestionString = props.term.term;
        newQuestionGroupString = props.term.group;
        break;
      case "definition":
        newQuestionString = props.term.definition;
        break;
      case "pron":
        newQuestionString = t("GuessTheDefinitionWithPronunciation");
        newQuestionGroupString = props.term.group;
        break;
    }
    setQuestion(newQuestionString);
    setCurrentGroup(newQuestionGroupString);
  }, [currentQuestion, props.term, t]);

  function isShowMemo(): boolean {
    if (isResultMode) return true;
    const memoPositionValue = getMemoPositionFromLocalStorage(LearningType.multiplechoice);
    switch (memoPositionValue) {
      case MemoPosition.onTerm:
        return currentQuestion === "term" && props.term.memo.length !== 0;
      case MemoPosition.onDefinition:
        return currentQuestion === "definition" && props.term.memo.length !== 0;
      case MemoPosition.none:
        return false;
    }
  }

  // 리스너는 한 번만 등록하므로 최신 상태를 보는 핸들러를 ref 로 참조한다
  const handleOnPressNineKeyRef = useRef(handleOnPressNineKey);
  handleOnPressNineKeyRef.current = handleOnPressNineKey;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // 오답노트 입력칸에 숫자를 적을 때는 반응하지 않는다
      const target = event.target as HTMLElement | null;
      if (target?.tagName === "INPUT" || target?.tagName === "TEXTAREA") return;
      if (event.code === "Digit9" || event.code === "Numpad9") {
        handleOnPressNineKeyRef.current();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  function isShowPronunciation(): boolean {
    if (props.term.pronunciation.length === 0) return false;
    if (
      currentQuestion === "term" &&
      getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onTerm
    ) {
      return true;
    } else if (
      currentQuestion === "definition" &&
      getLearningPronunciationPositionFromLocalStroage() ===
        PronunciationPosition.onDefinition
    ) {
      return true;
    } else {
      return false;
    }
  }

  function isAnswer(value?: string) {
    switch (currentQuestion) {
      case "term":
      case "pron":
        return props.term.definition === value;
      case "definition":
        return props.term.term === value;
    }
  }

  function handleOnClickMarkButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    props.onClickMarkButton(props.term);
  }

  function handleOnClickSpeakButton(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    speak();
  }

  function handleOnClickAnswerButton(isAnswer: boolean, answer: string) {
    const value = getLearningAnswerSpeechFromLocalStroage();

    if (isAnswer) {
      if (
        isOnLearningTextToSpeechFromLocalStorage() &&
        value === LearningAnswerSpeech.always
      ) {
        speak();
      }
    } else {
      if (
        isOnLearningTextToSpeechFromLocalStorage() &&
        (value === LearningAnswerSpeech.always ||
          value === LearningAnswerSpeech.onlyIncorrect)
      ) {
        speak();
      }
    }

    setIsResultMode(true);
    setIsAnswerResult(isAnswer);
    setSelectedAnswer(answer);
    if (isAnswer) {
      delay(() => {
        props.onCorrect(props.term);
      }, 1000);
    }
  }

  function handleOnClickPassButton() {
    skip();
  }

  function skip() {
    if (isResultMode) return;
    setIsResultMode(true);
    setIsAnswerResult(false);
  }

  function handleOnClickContinueButton() {
    // 모르겠어요/시간 초과는 앱과 동일하게 오답노트를 띄우지 않는다
    if (
      selectedAnswer !== null &&
      isOnWrongAnswerNoteFromLocalStorage(LearningType.multiplechoice)
    ) {
      setIsShowingWrongAnswerNote(true);
      return;
    }
    props.onIncorrect(props.term);
  }

  function handleOnPressEnterKey() {
    if (isShowingWrongAnswerNote) return;
    if (isResultMode === true && isAnswerResult !== true) {
      handleOnClickContinueButton();
    }
  }

  function handleOnPressNineKey() {
    if (isShowingWrongAnswerNote) return;
    if (!isResultMode) {
      handleOnClickPassButton();
    } else if (isAnswerResult !== true) {
      handleOnClickContinueButton();
    }
  }

  async function speak() {
    setSpeakCount(speakCount + 1);
    setIsPlaying(true);
    try {
      const languageCode =
        currentGroup.length !== 0
          ? await fetchGroupLanguageCode(currentGroup)
          : "";

      const text = isHiraganaOnly(props.term.pronunciation)
        ? props.term.pronunciation
        : props.term.term;
      const audio = await getVoiceFile(text, languageCode);
      setAudioSrc(makeBase64String(audio));
    } catch (error) {
      if (error instanceof Error) {
        props.onSpeakError(error.message);
      }
    }
  }

  function reload() {
    setIsAnswerResult(false);
    setIsResultMode(false);
    setSelectedAnswer(null);
    setIsShowingWrongAnswerNote(false);
    setSpeakCount(0);
    updateCurrentQuestionType();
    restartTimeLimit();
  }

  function restartTimeLimit() {
    setTimeLimitSecond(getMultipleChoiceTimeLimitValue());
    setTimeLimitRestartToken((token) => token + 1);
  }

  function handleOnTimeLimitTimeout() {
    const value = getLearningAnswerSpeechFromLocalStroage();
    if (
      isOnLearningTextToSpeechFromLocalStorage() &&
      (value === LearningAnswerSpeech.always ||
        value === LearningAnswerSpeech.onlyIncorrect)
    ) {
      speak();
    }
    handleOnClickPassButton();
  }

  function getCurrentQuestionType(): "term" | "definition" | "pron" {
    switch (getQuestionTypeFromLocalStorage(LearningType.multiplechoice)) {
      case QuestionType.termFirst:
      case QuestionType.showAll:
        return "term";
      case QuestionType.definitionFirst:
        return "definition";
      case QuestionType.pronFirst:
        return "pron";
      case QuestionType.random:
        return randomChoice;
    }
  }

  function updateCurrentQuestionType() {
    switch (getQuestionTypeFromLocalStorage(LearningType.multiplechoice)) {
      case QuestionType.termFirst:
        setCurrentQuestion("term");
        break;
      case QuestionType.definitionFirst:
        setCurrentQuestion("definition");
        break;
      case QuestionType.pronFirst:
        setCurrentQuestion("pron");
        break;
      case QuestionType.random:
        setCurrentQuestion(randomChoice);
        break;
    }
  }

  const answerTitleTextClass = classNames(styles.answerTitleText, {
    [styles.answerTitleTextCorrect]:
      isAnswerResult === true && isResultMode === true,
    [styles.answerTitleTextIncorrect]:
      isAnswerResult === false && isResultMode === true,
  });

  const passButtonClass = classNames(styles.passButton, {
    [styles.passButtonDisable]: isResultMode,
  });

  if (isShowingWrongAnswerNote) {
    return (
      <WrongAnswerNote
        term={props.term}
        userAnswer={selectedAnswer ?? ""}
        englishPronunciation={props.englishPronunciation}
        showingPronunciationSelectView={props.showingPronunciationSelectView}
        onClickMarkButton={props.onClickMarkButton}
        onClickPronunciationSelectButton={
          props.onClickPronunciationSelectButton
        }
        onSpeakError={props.onSpeakError}
        onFinish={() => props.onIncorrect(props.term)}
      />
    );
  }

  return (
    <>
      <motion.div
        className={styles.containerForMotion}
        key={props.term.id}
        initial={{ x: 100, opacity: 0 }}
        animate={{
          rotateX: 0,
          x: 0,
          opacity: 1,
        }}
        transition={{
          duration: 0.3,
        }}
        style={{
          transformStyle: "preserve-3d",
        }}
      >
        <div className={styles.container}>
          <div className={styles.topContainer}>
            <div className={styles.topLeftContainer}>
              <MarkButton
                isMarked={props.term.isMarked}
                onClick={handleOnClickMarkButton}
              />
              <SpeakButton
                fontSize="small"
                isPlaying={isPlaying}
                onClick={handleOnClickSpeakButton}
                title={speechShourtCut}
              />
            </div>
            <div className={styles.topRightContainer}>
              <PronunciationSelectView
                open={props.showingPronunciationSelectView}
                selectedPronunciation={props.englishPronunciation}
                onClickAction={(
                  e: React.MouseEvent<HTMLDivElement>,
                  pronunciation: EnglishPronunciation
                ) => {
                  handleOnClickSpeakButton(e);
                  props.onClickPronunciationSelectButton(pronunciation);
                }}
              />
              <LearningTimeLimitView
                limitSecond={timeLimitSecond}
                restartToken={timeLimitRestartToken}
                isStopped={isResultMode}
                isPaused={props.isPaused}
                onTimeout={handleOnTimeLimitTimeout}
              />
            </div>
          </div>
          <div
            className={styles.contentsContainer}
            lang={
              chineseCharacterMode === "japanese" ? "ja" : navigator?.language
            }
          >
            <div className={styles.questionContainer}>
              {isShowPronunciation() === true && (
                <p className={styles.pronunciationText}>
                  {`[${props.term.pronunciation}]`}
                </p>
              )}
              <p className={styles.questionText}>{question}</p>
              {isShowMemo() && (
                <p className={styles.memoText}>{props.term.memo}</p>
              )}
            </div>
            <div className={styles.answerContainer}>
              <p className={answerTitleTextClass}>
                {isResultMode === true
                  ? isAnswerResult
                    ? t("CorrectAnswerMessage")
                    : t("IncorrectAnswerMessage")
                  : t("ChooseCorrectDefinitionMessage")}
              </p>
              <div className={styles.answerButtonsContainer}>
                <div className={styles.answerButtonsFirstLine}>
                  <MultipleChoiceAnswerButton
                    isAnswer={isAnswer(answers[0])}
                    isResultMode={isResultMode}
                    value={answers[0]}
                    buttonNumber={1}
                    onClick={(isAnswer) =>
                      handleOnClickAnswerButton(isAnswer, answers[0])
                    }
                  />
                  <MultipleChoiceAnswerButton
                    isAnswer={isAnswer(answers[1])}
                    isResultMode={isResultMode}
                    value={answers[1]}
                    buttonNumber={2}
                    onClick={(isAnswer) =>
                      handleOnClickAnswerButton(isAnswer, answers[1])
                    }
                  />
                </div>
                <div className={styles.answerButtonsSecondLine}>
                  <MultipleChoiceAnswerButton
                    isAnswer={isAnswer(answers[2])}
                    isResultMode={isResultMode}
                    value={answers[2]}
                    buttonNumber={3}
                    onClick={(isAnswer) =>
                      handleOnClickAnswerButton(isAnswer, answers[2])
                    }
                  />
                  <MultipleChoiceAnswerButton
                    isAnswer={isAnswer(answers[3])}
                    isResultMode={isResultMode}
                    value={answers[3]}
                    buttonNumber={4}
                    onClick={(isAnswer) =>
                      handleOnClickAnswerButton(isAnswer, answers[3])
                    }
                  />
                </div>
              </div>
              <div className={styles.passButtonContainer}>
                {!isResultMode && (
                  <button
                    className={passButtonClass}
                    onClick={() => handleOnClickPassButton()}
                  >
                    {"(9) " + t("PassButton")}
                  </button>
                )}
                {isResultMode === true && isAnswerResult !== true && (
                  <button
                    className={styles.continueButton}
                    onClick={() => handleOnClickContinueButton()}
                  >
                    {t("Continue")}
                  </button>
                )}
              </div>
            </div>
          </div>
          {audioSrc && (
            <AudioPlayer
              autoPlay={true}
              src={audioSrc}
              style={{ display: "none" }}
              onEnded={() => {
                setIsPlaying(false);
                setAudioSrc(undefined);
              }}
            />
          )}
        </div>
      </motion.div>
      <EnterKeyListenerComponent onEnterPress={() => handleOnPressEnterKey()} />
    </>
  );
}
